import {AttachmentRef, Maybe, PhaseDate} from 'types';
import i18n from 'i18n/i18n';
import {TFunction} from 'react-i18next';
import {formatDateTime} from 'i18n/localeDateFormat';
import Excel, {RichText} from 'exceljs';
import JSZip from 'jszip';
import {saveAs} from 'file-saver';
import {bImageXL} from './ExcelLogo';
import {InfinityListColumn} from '../InfinityList';

export interface IArrayRoles {
	isAdmin: boolean;
	isVko: boolean;
	isVex: boolean;
	isReader: boolean;
	isBedarfskoordinator: boolean;
}

export interface IHTMLXL {
	font: IDictFont;
	text: string;
}

export interface XLColumn {
	name: string;
	filterButton: boolean;
	xlWidth?: number;
	xlRender?: (x: any) => any;
}

interface IDictFont {
	bold: boolean;
	underline?: boolean;
	italic?: boolean;
	strike?: boolean;
	vertAlign?: string | undefined;
	name: string;
	size: number;
}

export const porscheFontXL = () => {
	return {bold: true, name: 'Porsche Next TT', size: 10};
};

export const addXLTable = (
	tableName: string,
	intRowCount: number,
	sheet: Excel.Worksheet,
	columns: Excel.TableColumnProperties[],
) => {
	sheet.addTable({
		name: tableName,
		ref: 'B' + String(intRowCount),
		style: {
			showRowStripes: true,
		},
		columns,
		rows: [],
	});
};

export const renderDateXL = (date: string | undefined): string => {
	return date ? formatDateTime(new Date(date), i18n) : '-';
};

export const renderPhaseDateXL = (
	phaseDates: Maybe<PhaseDate[]> | undefined,
): string => {
	return renderStringArrayXL(
		(phaseDates ?? []).map(PI => `${renderDateXL(PI.date)} (${PI.status})`),
	);
};

export const renderStringArrayXL = (
	array: Maybe<string[]> | undefined,
): string => {
	const arrayFinal = array ?? [];
	if (arrayFinal.length === 0) {
		return '-';
	}
	return arrayFinal.join(';\n');
};

export const renderArrayXL = (array: Maybe<any[]> | undefined): string => {
	return renderStringArrayXL((array ?? []).map(x => x.name));
};

export const renderArrayAsRichtText = (
	heading: string,
	array: any[],
): {richText: Excel.RichText[]} => {
	const content = renderArrayXL(array);
	return renderRichtText(heading, content === '' ? '-' : content);
};

export const renderHTMLStringXL = (
	heading: string,
	htmlString: Maybe<string> | undefined,
) => {
	const tempDivElement = document.createElement('div');
	tempDivElement.innerHTML = htmlString
		? '<div>' + htmlString + '</div>'
		: '<div></div>';
	const arrHTMLXL: IHTMLXL[] = [];
	const arrHTMLXLClean: IHTMLXL[] = [];
	renderHTMLExcel(tempDivElement.childNodes[0].childNodes, arrHTMLXL);
	cleanHTMLExcel(arrHTMLXL, arrHTMLXLClean);
	return {
		wrapText: true,
		vertical: 'top',
		richText: [
			...(heading != ''
				? [
						{
							font: porscheFontXL(),
							text: heading + '\n',
						},
				  ]
				: []),
			...(arrHTMLXLClean as RichText[]),
		],
	};
};

export const renderRichtText = (heading: string, content: string) => {
	const fontHeading = {...porscheFontXL(), bold: true};
	const fontText = {...porscheFontXL(), bold: false};

	return {
		richText: [
			{
				font: fontHeading,
				text: heading + '\n',
			},
			{
				font: fontText,
				text: content,
			},
		],
	};
};

export const renderHeaderXL = (
	workbook: Excel.Workbook,
	sheet: Excel.Worksheet,
	arrColWidth: number[],
	strPageName: string,
	t: TFunction,
) => {
	workbook.properties.date1904 = true;
	workbook.calcProperties.fullCalcOnLoad = false;
	sheet.properties.outlineProperties = {
		summaryBelow: false,
		summaryRight: false,
	};

	arrColWidth.forEach((x, i) => {
		sheet.getColumn(i + 2).font = {...porscheFontXL(), bold: false};
		sheet.getColumn(i + 2).width = x;
	});

	let intRowCount = 1;
	let row = sheet.getRow(intRowCount);

	const imageId2 = workbook.addImage({
		base64: bImageXL,
		extension: 'png',
	});

	sheet.addImage(imageId2, {
		tl: {col: 0, row: 0},
		ext: {width: 310, height: 60},
		editAs: 'oneCell',
		hyperlinks: {
			hyperlink: window.location.href,
			tooltip: 'TRACE',
		},
	});

	row.height = 70.0;
	row.fill = {
		type: 'pattern',
		pattern: 'solid',
		fgColor: {argb: '303030'},
	};

	intRowCount++;
	row = sheet.getRow(intRowCount);
	row.alignment = {wrapText: true, vertical: 'top'};
	row.getCell('B').value = {
		richText: [
			{
				font: porscheFontXL(),
				text: strPageName,
			},
		],
	};

	row.getCell('E').value = renderRichtText(
		t('ExcelDate', {ns: 'components/EntityList/ExcelExport'}),
		formatDateTime(new Date(), i18n),
	);
};

export const renderAttachmentsXL = (
	intRowCount: number,
	label: string,
	attachments: Maybe<AttachmentRef[]> | undefined,
	sheet: Excel.Worksheet,
	t: TFunction,
) => {
	intRowCount++;
	let row = sheet.getRow(intRowCount);
	row.alignment = {wrapText: true, vertical: 'top'};
	row.getCell('B').value = renderRichtText(
		label,
		(attachments ?? []).length.toString(),
	);

	const tooltip = t('Link', {
		ns: 'components/EntityList/ExcelExport',
		keyPrefix: 'TooltipsText',
	});

	(attachments ?? []).forEach(iAttach => {
		intRowCount++;
		row = sheet.getRow(intRowCount);
		let attachCategory = iAttach.category?.name;
		attachCategory = attachCategory ? ` (${attachCategory})` : '';
		const attachText =
			(iAttach.file?.originalFileName ?? iAttach.attachmentId) + attachCategory;
		row.getCell('B').value = {
			text: attachText,
			hyperlink: iAttach.file.uri,
			tooltip,
		};
		row.getCell('B').style.font = {
			underline: true,
			bold: false,
			color: {argb: '00B0F0'},
		};
	});

	return intRowCount;
};

export const addHTMLImagesAsLinks = (
	intRowCount: number,
	requirementId: string,
	arrImagesName: string[],
	arrImagesUri: string[],
	imgCol: string,
	imngColId: number,
	tempDivElement: HTMLDivElement,
	sheet: Excel.Worksheet,
	table: Excel.Table,
	t: TFunction,
) => {
	for (
		let jj = 0;
		jj < tempDivElement.getElementsByTagName('img').length;
		jj++
	) {
		arrImagesUri.push(
			tempDivElement
				.getElementsByTagName('img')[0]
				.getAttribute('src') as string,
		);
		arrImagesName.push(requirementId + '_' + jj + '.html');
		const text = t('LinkImage', {ns: 'components/EntityList/ExcelExport'});
		const hyperlink =
			(('assets/' + requirementId) as string) + '_' + jj + '.html';
		intRowCount = renderRowItemAsLinkXL(
			intRowCount,
			text,
			hyperlink,
			imgCol,
			imngColId,
			sheet,
			table,
			t,
		);
	}
	return intRowCount;
};

export const renderRowItemAsLinkXL = (
	intRowCount: number,
	text: string,
	hyperlink: string,
	linkCol: string,
	linkColId: number,
	sheet: Excel.Worksheet,
	table: Excel.Table,
	t: TFunction,
) => {
	const row = sheet.getRow(intRowCount);
	row.outlineLevel = 1;
	row.getCell('B').fill = {
		type: 'pattern',
		pattern: 'solid',
		fgColor: {argb: '8DB4E2'},
	};
	row.getCell(linkCol).alignment = {
		indent: 1,
		wrapText: true,
		vertical: 'top',
	};
	row.getCell(linkCol).style.font = {
		underline: true,
		bold: false,
		color: {argb: '00B0F0'},
	};
	table.addRow([
		...new Array(linkColId).fill(''),
		{
			text,
			hyperlink,
			tooltip: t('Link', {
				ns: 'components/EntityList/ExcelExport',
				keyPrefix: 'TooltipsText',
			}),
		},
	]);
	intRowCount++;
	return intRowCount;
};

export const renderHTMLExcel = (
	arrChildNodes: NodeListOf<ChildNode>,
	arrHTMLXL: IHTMLXL[],
	dictFontParent?: IDictFont | undefined,
) => {
	for (let ii = 0; ii < arrChildNodes.length; ii++) {
		const dictFont = {
			name: 'Porsche Next TT',
			bold: dictFontParent
				? dictFontParent.bold
				: arrChildNodes[ii].nodeName === 'b'.toUpperCase() ||
				  arrChildNodes[ii].nodeName === 'strong'.toUpperCase() ||
				  arrChildNodes[ii].nodeName === 'h2'.toUpperCase(),
			underline: dictFontParent
				? dictFontParent.underline
				: arrChildNodes[ii].nodeName === 'i'.toUpperCase() ||
				  arrChildNodes[ii].nodeName === 'u'.toUpperCase(),
			italic: dictFontParent ? dictFontParent.italic : false,
			strike: dictFontParent ? dictFontParent.strike : false,

			size: arrChildNodes[ii].nodeName === 'h2'.toUpperCase() ? 12 : 10,
		};

		if (arrChildNodes[ii].nodeName === 'li'.toUpperCase()) {
			arrHTMLXL.push({font: dictFont, text: ' •'});
		}

		if (arrChildNodes[ii].nodeName === '#text') {
			let strText = arrChildNodes[ii].textContent ?? '\n';
			strText = strText.trim();
			arrHTMLXL.push({
				font: dictFont,
				text: strText === '' ? '\n' : decodeHtmlEntitiesXL(strText),
			});
		}

		if (arrChildNodes[ii].childNodes.length > 0) {
			renderHTMLExcel(arrChildNodes[ii].childNodes, arrHTMLXL, dictFont);
		}

		if (arrChildNodes[ii].nodeName === 'p'.toUpperCase()) {
			arrHTMLXL.push({font: dictFont, text: '\n'});
		}

		if (arrChildNodes[ii].nodeName === 'br'.toUpperCase()) {
			arrHTMLXL.push({font: dictFont, text: '\n'});
		}

		if (arrChildNodes[ii].nodeName === 'li'.toUpperCase()) {
			arrHTMLXL.push({font: dictFont, text: '\n'});
		}
	}
};

export const mergeColumnsExcel = (
	columns: InfinityListColumn[],
	columnsXLRender: any[],
) =>
	columns.map(x => {
		const columnXLRender = columnsXLRender.find(y => x.key === y.key);
		return {
			name: columnXLRender?.xlName ?? x.name,
			filterButton: !(columnXLRender.xlHideFilter == true),
			xlRender: columnXLRender?.xlRender,
			xlWidth: columnXLRender?.xlWidth,
		};
	});

export const cleanHTMLExcel = (
	arrHTMLXL: IHTMLXL[],
	arrHTMLXLClean: IHTMLXL[],
) => {
	let arrHTMLXLTrim = arrHTMLXL.map(x => ({...x, text: x.text.trim()}));

	// remove line breaks at front of array
	let i = 0;
	while (i < arrHTMLXLTrim.length && arrHTMLXLTrim[i].text === '') {
		i++;
	}
	arrHTMLXLTrim = arrHTMLXLTrim.slice(i);

	//remove 2 consective line breaks
	for (let jj = 0; jj < arrHTMLXLTrim.length; jj++) {
		if (arrHTMLXLTrim[jj].text === '') {
			const boolSkip =
				arrHTMLXLTrim[jj - 1]?.text === '' &&
				arrHTMLXLTrim[jj - 2]?.text === '';
			if (!boolSkip) {
				const x = arrHTMLXLTrim[jj];
				arrHTMLXLClean.push({...x, text: '\n'});
			}
		} else {
			arrHTMLXLClean.push(arrHTMLXLTrim[jj]);
		}
	}
};

export const createZIPXL = async (
	workbook: Excel.Workbook,
	strZipName: string,
	t: TFunction,
	arrTables?: string[] | undefined,
	arrImagesUri?: string[] | undefined,
	arrImagesName?: string[] | undefined,
	arrAttachments?: AttachmentRef[] | undefined,
) => {
	const strDate = new Date()
		.toISOString()
		.replace('T', ' ')
		.slice(0, 10)
		.replaceAll('-', '');

	const buffer = await workbook.xlsx.writeBuffer();
	const blobXL = new Blob([buffer], {
		type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	});
	const blobMSG = new Blob([''], {type: 'text'});
	const zip = new JSZip();

	(arrTables ?? [])
		.map(strHTMLTable => new Blob([strHTMLTable], {type: 'text.txt'}))
		.map((blob, intID) =>
			zip.file('assets/html_table_' + String(intID) + '.html', blob),
		);

	(arrImagesUri ?? [])
		.map(strImageUri => {
			const strHTML = `<!DOCTYPE html><html><body><img src="${strImageUri}" ></body></html>`;
			return new Blob([strHTML], {type: 'text.txt'});
		})
		.map((blob, intID) =>
			zip.file('assets/' + String(arrImagesName![intID]), blob),
		);

	zip.file(
		strDate + '_' + i18n.language + '_TRACE_' + strZipName + '.xlsx',
		blobXL,
	);
	zip.file(
		t('ExcelExtract', {ns: 'components/EntityList/ExcelExport'}),
		blobMSG,
	);
	const objZip = await zip.generateAsync({type: 'blob'});
	const blobZip = new Blob([objZip], {type: 'application/zip'});
	const fileName =
		strDate + '_' + i18n.language + '_TRACE_EXPORT_' + strZipName + '.zip';
	saveAs(blobZip, fileName);
};

export function decodeHtmlEntitiesXL(input: string): string {
	const textarea = document.createElement('textarea');
	textarea.innerHTML = input;
	return textarea.value;
}
